import React, {useEffect, useRef} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as EditIcon} from 'assets/svg/edit.svg';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {
    $invoiceLineChargeData,
    $invoiceLinesHolder,
    setInvoiceLineChargeData,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';
import {setInlineInvoiceChargeModal} from 'models/documents/invoices/details';

import {useFormLineCalculations} from '../../../hooks/useFormLineCalculations';
import {useLineCalculations} from '../../../hooks/useLineCalculations';

import {ChargesControl, ControlContainer} from './styled';

export const ModalHandler = ({
    index,
    getValues,
    setValue,
    trigger,
}: {
    index?: number;
    getValues?: (x?: string | string[]) => any;
    setValue?: (x: string, q: string) => any;
    trigger?: (name?: string | string[] | undefined) => Promise<boolean>;
}) => {
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const invoiceLineChargeData = useStore($invoiceLineChargeData);
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const lineCalculations = useLineCalculations();
    const formLineCalculations = useFormLineCalculations({
        getValues,
        setValue,
        trigger,
    });

    const combineValues =
        index !== undefined ? lineCalculations.combineValues : formLineCalculations.combineValues;

    const calculateLineAmountWVat =
        index !== undefined
            ? lineCalculations.calculateLineAmountWVat
            : formLineCalculations.calculateLineAmountWVat;

    const deleteInvoiceLineItem = (removeFn: () => void) => (
        <div style={{cursor: 'pointer', margin: 'auto'}}>
            <Icon
                source={CrossIcon}
                width={16}
                height={16}
                fillColor="#FF4B55"
                onClick={() => {
                    removeFn();
                }}
            />
        </div>
    );

    const editInvoiceLine = (edit: () => void) => (
        <div style={{cursor: 'pointer', margin: 'auto'}}>
            <Icon
                source={EditIcon}
                width={28}
                height={28}
                fillColor="#FF4B55"
                onClick={() => {
                    edit();
                }}
            />
        </div>
    );

    const item = index === undefined ? invoiceLineChargeData : invoiceLinesHolder[index]?.charges;

    const prevItemRef = useRef(item);
    const itemChanged = useRef(false);

    useEffect(() => {
        const itemStringified = JSON.stringify(item);
        const prevItemStringified = JSON.stringify(prevItemRef.current);

        if (itemStringified !== prevItemStringified && !itemChanged.current) {
            if (index !== undefined) {
                combineValues(index);
                calculateLineAmountWVat(index);
            } else if (item) {
                combineValues(undefined);
                calculateLineAmountWVat(undefined);
            }
        }

        prevItemRef.current = item;
        itemChanged.current = false;
    }, [item, index]);

    if (!item) {
        return (
            <ChargesControl justify="space-between">
                <Button
                    type="button"
                    style={{width: 'auto'}}
                    size="sm"
                    variant={ButtonVariant.Outline}
                    onClick={() => {
                        setInlineInvoiceChargeModal({
                            index,
                            show: true,
                        });
                    }}
                >
                    {t('addToLine')}
                </Button>
            </ChargesControl>
        );
    }

    return (
        <ChargesControl>
            <div>{item.isCharge === 'false' ? '-' : '+'}</div>
            <div>{Number(item.amountXaf).toFixed(1)}</div>
            <div>{item.ratePercent ? `${item.ratePercent}%` : ''}</div>
            <ControlContainer>
                {editInvoiceLine(() => {
                    setInlineInvoiceChargeModal({
                        index,
                        show: true,
                    });
                })}
                {deleteInvoiceLineItem(() => {
                    if (index !== undefined) {
                        updateInvoiceLinesHolder({
                            index,
                            data: {columnId: 'charges', value: null},
                        });
                        itemChanged.current = true;
                        combineValues(index);
                        calculateLineAmountWVat(index);
                    } else {
                        setInvoiceLineChargeData(null);
                        itemChanged.current = true;
                        combineValues(index);
                        calculateLineAmountWVat(index);
                    }
                })}
            </ControlContainer>
        </ChargesControl>
    );
};
